<template>
  <div class="ui-image-upload" :data-value="committedValue" :class="{ 'is-full': hideUpload, 'is-disabled': disabled }">
    <el-upload
      ref="upload"
      name="file"
      list-type="picture-card"
      :action="action"
      :headers="headers"
      :accept="accept"
      :multiple="multiple"
      :limit="effectiveLimit || undefined"
      :disabled="disabled"
      :file-list="displayFiles"
      :before-upload="beforeUpload"
      :on-change="handleChange"
      :on-success="handleSuccess"
      :on-error="handleError"
      :on-remove="handleRemove"
      :on-preview="handlePreview"
      :on-exceed="handleExceed"
    >
      <template #file="{ file }">
        <div class="ui-image-upload__card">
          <img
            class="el-upload-list__item-thumbnail ui-image-upload__image"
            :class="{ 'is-visible': !isFilePending(file) && imageState(file) === 'loaded' }"
            :src="file.url"
            :alt="file.name || $t('图片预览')"
            :aria-hidden="isFilePending(file) || imageState(file) !== 'loaded'"
            @load="handleImageState(file, true)"
            @error="handleImageState(file, false)"
          >
          <div
            class="ui-image-upload__progress"
            :class="{ 'is-hidden': !isFilePending(file) && imageState(file) !== 'loading' }"
            :aria-hidden="!isFilePending(file) && imageState(file) !== 'loading'"
            :aria-label="$t(isFilePending(file) ? '图片上传中' : '图片加载中')"
          >
            <UiProgress type="circle" :stroke-width="6" :width="100" :percentage="fileProgress(file)" />
          </div>
          <div
            class="ui-image-upload__preview-error"
            :class="{ 'is-visible': !isFilePending(file) && imageState(file) === 'error' }"
            :aria-hidden="isFilePending(file) || imageState(file) !== 'error'"
          >{{ $t("图片预览加载失败") }}</div>
          <label
            class="el-upload-list__item-status-label ui-image-upload__status"
            :class="{ 'is-visible': !isFilePending(file) && imageState(file) === 'loaded' }"
            :aria-label="$t('图片已上传')"
            :aria-hidden="isFilePending(file) || imageState(file) !== 'loaded'"
          ><i class="el-icon-upload-success el-icon-check"></i></label>
          <div class="ui-image-upload__actions" :class="{ 'is-pending': isFilePending(file) }">
            <UiControlGroup :aria-label="$t('图片操作')">
              <UiIconControl
                v-if="!isFilePending(file) && imageState(file) === 'loaded'"
                size="small"
                icon="el-icon-zoom-in"
                :title="$t('预览图片')"
                :aria-label="$t('预览图片')"
                @click.stop="handlePreview(file)"
              />
              <UiIconControl
                v-if="!disabled"
                size="small"
                icon="el-icon-delete"
                :title="$t(isFilePending(file) ? '取消上传' : '删除图片')"
                :aria-label="$t(isFilePending(file) ? '取消上传' : '删除图片')"
                @click.stop="requestRemove(file)"
              />
            </UiControlGroup>
          </div>
        </div>
      </template>
      <i class="el-icon-plus" aria-hidden="true"></i>
      <span class="sr-only">{{ $t("上传图片") }}</span>
    </el-upload>
    <div class="form-tip">{{ $t(helpText) }}</div>
    <UiDialog v-model="previewVisible" :title="$t('图片预览')">
      <img class="ui-image-upload__preview" :src="previewUrl" :alt="$t('图片预览')">
    </UiDialog>
  </div>
</template>

<script>
import { t } from '@/i18n'
import { getImageUploadError, IMAGE_MIME_TYPES } from '@/utils/imageUpload'

export default {
  name: 'UiImageUpload',
  props: {
    value: { type: Array, default: () => [] },
    imageUrl: { type: String, default: '' },
    action: { type: String, required: true },
    headers: { type: Object, default: () => ({}) },
    formats: { type: Array, default: () => ['jpg', 'png', 'gif', 'webp'] },
    maxSizeMb: { type: Number, default: 2, validator: value => Number.isFinite(value) && value > 0 },
    multiple: { type: Boolean, default: false },
    limit: { type: Number, default: 0, validator: value => Number.isInteger(value) && value >= 0 },
    recommendation: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return { pendingFiles: [], completedFiles: [], singleCompleted: null, fileOrder: [], imageStates: {}, previewVisible: false, previewUrl: '' }
  },
  computed: {
    committedValue () { return JSON.stringify(this.storedFiles.map(file => file.storedName || file.url)) },
    allowedFormats () { return this.formats.map(format => String(format).toLowerCase()).filter(format => IMAGE_MIME_TYPES[format]) },
    mimeTypes () { return [...new Set(this.allowedFormats.map(format => IMAGE_MIME_TYPES[format]))] },
    accept () { return this.allowedFormats.map(format => '.' + format).concat(this.mimeTypes).join(',') },
    effectiveLimit () { return this.multiple ? this.limit : 1 },
    storedFiles () {
      if (this.multiple) return this.value.concat(this.completedFiles.filter(file => !this.value.some(item => item.uid === file.uid)))
      // 保留上传卡片的 uid，等待父表单回传地址时也不重新插入一张卡。
      if (this.singleCompleted) return [this.singleCompleted]
      return this.imageUrl ? [{ uid: 'saved-image', name: '图片', url: this.imageUrl, status: 'success' }] : []
    },
    displayFiles () { return this.orderFiles(this.storedFiles.concat(this.pendingFiles.filter(file => !this.storedFiles.some(item => item.uid === file.uid)))) },
    isFull () { return this.effectiveLimit > 0 && this.displayFiles.length >= this.effectiveLimit },
    hideUpload () { return this.isFull || this.pendingFiles.length > 0 },
    helpText () {
      const recommendation = this.recommendation ? this.recommendation + '；' : ''
      const count = this.multiple ? (this.limit ? '最多 ' + this.limit + ' 张图片' : '支持上传多张图片') : '限 1 张图片，更换请先删除原图'
      return recommendation + '支持 ' + this.allowedFormats.join('/').toUpperCase() + '，单张不超过 ' + this.maxSizeMb + 'MB；' + count + '，可点击预览或删除。'
    }
  },
  watch: {
    displayFiles (files) {
      const activeIds = new Set(files.map(file => String(file.uid)))
      Object.keys(this.imageStates).forEach(uid => {
        if (!activeIds.has(uid)) this.$delete(this.imageStates, uid)
      })
    },
    // v-model 的 prop 回传可能晚于同一轮内多个上传回调，临时保留已完成项避免丢图。
    value () {
      this.completedFiles = []
      if (!this.pendingFiles.length) this.fileOrder = this.value.map(file => file.uid)
    },
    imageUrl (value, previous) {
      if (this.singleCompleted && ((value && value !== this.singleCompleted.url) || (!value && previous))) {
        this.singleCompleted = null
      }
    }
  },
  beforeDestroy () {
    if (this.$refs.upload) this.$refs.upload.abort()
    this.$emit('uploading', false)
  },
  methods: {
    notifyValueChange (phase) {
      const dispatch = () => {
        if (!this._isDestroyed && this.$el && this.$el.dispatchEvent) {
          this.$el.dispatchEvent(new CustomEvent('ui-image-upload-change', { bubbles: true, detail: { phase } }))
        }
      }
      if (phase === 'before') dispatch()
      else this.$nextTick(dispatch)
    },
    clearFiles () {
      if (this.$refs.upload) this.$refs.upload.abort()
      this.pendingFiles = []
      this.completedFiles = []
      this.singleCompleted = null
      this.fileOrder = []
      this.imageStates = {}
      this.$emit('uploading', false)
    },
    isFilePending (file) {
      // ready 也属于上传态，不能等第一个网络进度事件才隐藏图片。
      return file.status === 'ready' || file.status === 'uploading' || this.pendingFiles.some(item => item.uid === file.uid)
    },
    fileProgress (file) {
      if (!this.isFilePending(file)) return 100
      return Math.max(0, Math.min(100, Math.floor(Number(file.percentage) || 0)))
    },
    imageState (file) {
      const state = this.imageStates[file.uid]
      return state && state.url === file.url ? (state.loaded ? 'loaded' : 'error') : 'loading'
    },
    handleImageState (file, loaded) {
      const current = this.displayFiles.find(item => item.uid === file.uid)
      // 删除或切换图片后，不接受旧 URL 的迟到 load/error 事件。
      if (!current || current.url !== file.url) return
      this.$set(this.imageStates, file.uid, { url: file.url, loaded })
    },
    requestRemove (file) {
      // 必须经过 Element 的移除入口，同时中止真实上传请求。
      if (!this.disabled && this.$refs.upload) this.$refs.upload.handleRemove(file)
    },
    orderFiles (files) {
      // 上传完成次序不等于选择次序，不能把先完成的图片移到左边。
      return files.slice().sort((a, b) => this.fileOrder.indexOf(a.uid) - this.fileOrder.indexOf(b.uid))
    },
    beforeUpload (file) {
      const error = getImageUploadError(file, this.mimeTypes, this.allowedFormats.join('/').toUpperCase(), this.maxSizeMb)
      if (this.disabled || error) {
        this.removePending(file)
        if (error) this.$message.error(t(error))
        return false
      }
      return true
    },
    handleChange (file) {
      if (file.status !== 'ready') return
      if (!this.pendingFiles.some(item => item.uid === file.uid)) {
        this.fileOrder = this.displayFiles.map(item => item.uid).concat(file.uid)
        this.pendingFiles.push(file)
      }
      this.$emit('uploading', true)
    },
    removePending (file) {
      this.pendingFiles = this.pendingFiles.filter(item => item.uid !== file.uid)
      this.$emit('uploading', this.pendingFiles.length > 0)
    },
    handleSuccess (response, file) {
      if (!file || !this.pendingFiles.some(item => item.uid === file.uid)) return
      const storedName = response && typeof response.data === 'string' ? response.data : ''
      // 响应也只接受文件名；路径和内容安全最终由后端校验。
      if (!response || Number(response.status) !== 1 || storedName !== storedName.trim() || storedName.includes('..') || !/^[a-z0-9][a-z0-9_.-]*\.(?:jpe?g|png|gif|webp|svg)$/i.test(storedName)) {
        this.removePending(file)
        if (this.$refs.upload) this.$refs.upload.handleRemove(file)
        this.$message.error(t((response && response.msg) || '图片上传失败'))
        this.$emit('error', new Error('Invalid image upload response'))
        return
      }
      const settings = this.$store.state.settings
      const tempPath = settings.tempPath || settings.host + '/Data/UploadFiles/temp/'
      const completed = { uid: file.uid, name: storedName, url: tempPath + storedName, storedName, isNew: true, status: 'success', percentage: 100 }
      this.notifyValueChange('before')
      if (this.multiple) this.completedFiles.push(completed)
      else this.singleCompleted = completed
      // 先接续完成态，再移除上传态；整段过程始终只有同一个 key。
      this.removePending(file)
      if (this.multiple) this.$emit('input', this.orderFiles(this.storedFiles))
      this.$emit('success', response, file)
      this.notifyValueChange('after')
    },
    handleError (error, file) {
      if (!file || !this.pendingFiles.some(item => item.uid === file.uid)) return
      if (file) this.removePending(file)
      this.$message.error(t('图片上传失败，请重试'))
      this.$emit('error', error)
    },
    handleRemove (file) {
      const pending = this.pendingFiles.some(item => item.uid === file.uid)
      this.removePending(file)
      if (pending) return
      if (this.multiple) {
        const keep = item => file.uid && item.uid ? String(item.uid) !== String(file.uid) : item.url !== file.url
        const previous = this.storedFiles
        const next = previous.filter(keep)
        if (next.length !== previous.length) {
          this.notifyValueChange('before')
          this.completedFiles = this.completedFiles.filter(keep)
          this.$emit('input', next)
          this.notifyValueChange('after')
        }
      } else if (file.uid === 'saved-image' || file.url === this.imageUrl || (this.singleCompleted && file.uid === this.singleCompleted.uid)) {
        this.notifyValueChange('before')
        this.singleCompleted = null
        this.$emit('remove', file)
        this.notifyValueChange('after')
      }
    },
    handlePreview (file) { this.previewUrl = file.url; this.previewVisible = true },
    handleExceed () { this.$message.warning(t('最多上传 ' + this.effectiveLimit + ' 张图片')) }
  }
}
</script>

<style scoped>
/* 图片上传的唯一外观入口；独立弹窗挂到 body 后也不依赖后台容器选择器。 */
.ui-image-upload ::v-deep .el-upload--picture-card,
.ui-image-upload ::v-deep .el-upload-list--picture-card .el-upload-list__item {
  width: 148px;
  height: 148px;
  box-sizing: border-box;
  border-radius: var(--ui-radius-small);
  border: 1px dashed var(--ui-border-color);
  background: var(--ui-bg-control);
  color: var(--ui-text-secondary);
}
.ui-image-upload ::v-deep .el-upload--picture-card { display: inline-flex; align-items: center; justify-content: center; line-height: normal; animation: ui-image-upload-appear 200ms ease; }
.ui-image-upload ::v-deep .el-upload--picture-card > i { color: inherit; font-size: 28px; }
.ui-image-upload ::v-deep .el-upload--picture-card:hover,
.ui-image-upload ::v-deep .el-upload--picture-card:focus-visible { border-color: var(--ui-color-primary); color: var(--ui-color-primary); }
.ui-image-upload ::v-deep .el-upload-list__item-thumbnail { object-fit: contain; }
/* 卡片只改变透明度，不沿用 Element 的 translateY 和 transition: all。 */
.ui-image-upload ::v-deep .el-upload-list--picture-card .el-upload-list__item {
  position: relative;
  vertical-align: top;
  transition: opacity 200ms ease;
  transform: none !important;
}
.ui-image-upload ::v-deep .el-upload-list__item.el-list-enter,
.ui-image-upload ::v-deep .el-upload-list__item.el-list-leave-to { opacity: 0; }
.ui-image-upload__card { position: relative; width: 100%; height: 100%; }
.ui-image-upload__image,
.ui-image-upload__progress,
.ui-image-upload__preview-error {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: opacity 200ms ease;
}
.ui-image-upload__image,
.ui-image-upload__preview-error { opacity: 0; }
.ui-image-upload__image.is-visible,
.ui-image-upload__preview-error.is-visible { opacity: 1; }
.ui-image-upload__progress,
.ui-image-upload__preview-error { display: flex; align-items: center; justify-content: center; }
.ui-image-upload__progress { opacity: 1; pointer-events: none; }
.ui-image-upload__progress.is-hidden { opacity: 0; }
.ui-image-upload__preview-error { font-size: 12px; color: var(--ui-text-secondary); pointer-events: none; }
.ui-image-upload__status { opacity: 0; transition: opacity 200ms ease; }
.ui-image-upload__status.is-visible { opacity: 1; }
.ui-image-upload__progress ::v-deep .el-progress { position: static; width: auto; transform: none; }
.ui-image-upload__actions {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  background: rgba(var(--ui-bg-control-rgb), .82);
  transition: opacity 200ms ease;
}
.ui-image-upload__card:hover .ui-image-upload__actions,
.ui-image-upload__card:focus-within .ui-image-upload__actions { opacity: 1; }
.ui-image-upload__actions.is-pending { top: auto; padding: 4px; background: transparent; }
@keyframes ui-image-upload-appear {
  from { opacity: 0; }
  to { opacity: 1; }
}
@media (hover: none) {
  .ui-image-upload__actions { top: auto; padding: 4px; opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .ui-image-upload ::v-deep .el-upload--picture-card { animation: none; }
  .ui-image-upload ::v-deep .el-progress-circle__path { transition: none !important; }
  .ui-image-upload ::v-deep .el-upload-list--picture-card .el-upload-list__item,
  .ui-image-upload__image,
  .ui-image-upload__progress,
  .ui-image-upload__preview-error,
  .ui-image-upload__status,
  .ui-image-upload__actions { transition: none; }
}
/* 单图/数量上限是交互状态，不能被后台主题的 inline-flex 覆盖。 */
.ui-image-upload.is-full ::v-deep .el-upload--picture-card { display: none !important; }
.ui-image-upload.is-disabled ::v-deep .el-upload--picture-card { cursor: not-allowed; opacity: .6; }
.ui-image-upload .form-tip { margin-top: 8px; line-height: 1.6; }
.ui-image-upload__preview { display: block; max-width: 100%; margin: 0 auto; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
</style>
